import { store } from 'store';

import { CASE_MANAGEMENT_DIVISOR, CASE_MANAGEMENT_PERCENTAGE } from '~constants/common';
import { SupportAtHomeFundingTypes } from '~constants/maps';

import dayjs, { dateInputFieldFormat, formatDateString } from '~libs/dayjs';
import { formatServicePeriod } from '~support-at-home/lib/servicePeriod';

import { Row } from '~components/table-view';

import { PricePlanModalRow, SahServiceTypeAssociation } from '~support-at-home/models';

// Defined in a store-free module so a leaf column definition can reach them without pulling the
// Redux store in through this file. Re-exported here so existing callers are unchanged.
export { OPEN_ENDED_LABEL, formatServicePeriod } from '~support-at-home/lib/servicePeriod';

type Derive = (values: any) => any;
const currency = (value: number) => `$${Math.round(value).toLocaleString()}`;

export const derivePricePlan: Derive = ({ price_plan_id, price_plan_revision_id }) => ({
  price_plan_id,
  price_plan_revision_id,
});

export const deriveFormattedDateRange: Derive = ({ start_date, end_date }) => {
  if (!start_date || !end_date) return '';
  return `${formatDateString(start_date)} - ${formatDateString(end_date)}`;
};

export const deriveClaimStartDate: Derive = ({ end_date }) => {
  if (!end_date) return null;

  const date = dayjs(end_date);
  const SAH_QUARTER_START = '2025-10-01';
  const SAH_QUARTER_START_OVERRIDE = '2025-11-01';
  let start_date = date.startOf('quarter').format(dateInputFieldFormat);
  if (start_date === SAH_QUARTER_START) start_date = SAH_QUARTER_START_OVERRIDE;
  return { start_date, end_date };
};

export const deriveOriginLink: Derive = ({ origin_tag, origin_appointment_id, origin_supplier_invoice_item_id }) => {
  switch (origin_tag) {
    case 'invoice': {
      return `/support-at-home/suppliers?invoice-search={"items.id":"${origin_supplier_invoice_item_id}"}`;
    }
    case 'appointment': {
      return `/appointments/${origin_appointment_id}`;
    }
    default: {
      return null;
    }
  }
};

// Deep-links into a Support at Home config tab. The tab values are owned by ConfigContainer's
// TAB_PARAMS - without the `?tab=`, every one of these errors drops the user on the first tab and
// leaves them to work out which of the eight actually holds the thing they need to fix.
const configTab = (tab: string) => `/support-at-home/configs?tab=${tab}`;

export const deriveAlertLink: Derive = (row) => {
  const alertRef = row.alert_items?.[0]?.alert?.reference_id;
  if (!alertRef) return null;

  switch (alertRef) {
    // Budget listing (no specific budget to link to)
    case 'NO_ACTIVE_BUDGET_FOUND':
      return '/support-at-home/budgets';

    // Specific budget
    case 'NO_BILLING_RULE_FOUND':
    case 'BUDGET_NOT_ACTIVE':
    case 'NO_SHIFT_PRICE':
      return row.client_budget_id ? `/support-at-home/budgets/${row.client_budget_id}` : null;

    // SaH Configs, Service Type Assocs. tab - the association itself is what has to be corrected,
    // whether it is missing altogether or there is more than one covering the service date.
    case 'AMBIGUOUS_SAH_SERVICE_ASSOCIATION':
    case 'NO_SAH_SERVICE_ASSOCIATED':
      return configTab('associations');

    // SaH Configs, Price Plans tab - both of these are properties of the billing rule on the price
    // plan revision, not of the association, so the associations tab would be the wrong place to
    // land even though it is the same screen.
    case 'INVALID_BILLING_TYPE':
    case 'INVALID_RATE':
      return configTab('price-plans');

    // Appointment
    case 'INVALID_UNITS':
    case 'APT_NON_CHARGEABLE_CANCELLATION':
      return row.origin_appointment_id ? `/appointments/${row.origin_appointment_id}` : null;

    // Supplier invoice
    case 'NO_INVOICE_BUDGET_ASSOCIATED':
    case 'INVALID_INVOICE_UNITS':
    case 'INVALID_INVOICE_AMOUNT':
      return row.origin_supplier_invoice_item_id
        ? `/support-at-home/suppliers?invoice-search={"items.id":"${row.origin_supplier_invoice_item_id}"}`
        : null;

    // Wrap/item alerts — resolved during claim drafting, no link
    default:
      return null;
  }
};

export const showCaseManagement: Derive = ({ package_fixed_id }) => {
  const packageSchedule = findPackageSchedule(package_fixed_id);
  return packageSchedule?.package?.funding_type?.sah_code === SupportAtHomeFundingTypes.SAH_QUARTERLY_CLIENT;
};

export const showFundingLevelAmounts: Derive = ({ client_id }) => {
  return hasSahBudgets(client_id) === false;
};

export const deriveFundingType: Derive = ({ package_fixed_id }) => {
  const packageSchedule = findPackageSchedule(package_fixed_id);
  return packageSchedule?.package?.funding_type?.name;
};

export const deriveClientWarning: Derive = ({ client_id }) => {
  if (!client_id) return [];
  const { clientServices, clientContributions } = store.getState().supportAtHome;

  const warnings: string[] = [];

  const hasServices = clientServices.rows.some(({ client_id: id }) => id === client_id);
  const hasContributions = clientContributions.rows.some(({ client_id: id }) => id === client_id);

  if (!hasSahBudgets(client_id)) warnings.push('No SaH Budgets found for this client');
  if (!hasServices) warnings.push('No SaH Approved Services found for this client');
  if (!hasContributions) warnings.push('No SaH Co-Contributions found for this client');

  return warnings;
};

export const deriveCaseManagementValues: Derive = ({ total_includes_case_management, total_amount }) => {
  const parsedAmount = parseFloat(total_amount);

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    return {
      baseAmount: 0,
      caseManagementAmount: 0,
      baseAvailableAmount: 0,
    };
  }

  // Calculate base amount and case management amount based on whether CM is included
  let baseAmount: number;
  let caseManagementAmount: number;

  if (total_includes_case_management == null) {
    // If undefined or null: return base amount only, no partition of funds
    baseAmount = parsedAmount;
    caseManagementAmount = 0;
  } else if (total_includes_case_management) {
    // If CM is included in total: 10% of total is CM, 90% is base
    caseManagementAmount = parsedAmount * CASE_MANAGEMENT_PERCENTAGE;
    baseAmount = parsedAmount - caseManagementAmount;
  } else {
    // If CM is not included: total is the base, CM is added such that it equals 10% of the final total
    baseAmount = parsedAmount;
    caseManagementAmount = parsedAmount / CASE_MANAGEMENT_DIVISOR;
  }

  // Calculate base available amount (base + case management)
  const baseAvailableAmount = baseAmount + caseManagementAmount;

  return {
    baseAmount,
    caseManagementAmount,
    baseAvailableAmount,
  };
};

export const deriveCaseManagementWarning: Derive = ({ total_includes_case_management, total_amount }) => {
  const parsedAmount = parseFloat(total_amount);

  if (isNaN(parsedAmount) || parsedAmount <= 0) {
    if (parsedAmount === 0) return 'Enter a total amount or select an SAH budget to continue.';
    return 'Please enter a valid total amount greater than zero.';
  }

  const { baseAmount, caseManagementAmount, baseAvailableAmount } = deriveCaseManagementValues({
    total_includes_case_management,
    total_amount,
  });

  return `The base amount is ${currency(baseAmount)}. A 10% case management fee of ${currency(caseManagementAmount)} applies, making the total available amount ${currency(baseAvailableAmount)}.`;
};

export const deriveBaseAvailableAmount: Derive = (values) => deriveCaseManagementValues(values).baseAvailableAmount;

export const deriveStatusNeedsReviewWarning: Derive = ({ needs_review }) =>
  needs_review ? 'This budget must be reviewed and saved before its status can be changed.' : undefined;

// Helper functions
const findPackageSchedule = (package_fixed_id: any) => {
  const { clientPackageSchedules } = store.getState().main;
  return clientPackageSchedules.rows.find(({ package: { id } }) => Number(package_fixed_id) === id);
};

const hasSahBudgets = (client_id: number) => {
  const { sahBudgets } = store.getState().supportAtHome;
  return sahBudgets.rows.some(({ client_id: id }) => id === client_id);
};

// Price plan validator helpers

/**
 * Find the service-type association a price plan entry belongs to.
 *
 * A local service type can now be associated with more than one Support at Home service, one per
 * date period, so matching on the service type alone is ambiguous - it would return whichever
 * association happened to sort first and mark the other entry as mismatched. The entry's own
 * category is what identifies its period: two periods of the same service exist precisely because
 * their contribution categories differ, so the (service type, category) pair resolves to exactly
 * one association.
 */
export const findRowAssoc = (row: PricePlanModalRow | Row, serviceTypeAssocs: SahServiceTypeAssociation[]) => {
  const { service_type_id, category_id } = row ?? {};

  return serviceTypeAssocs.find(
    (assoc) => assoc?.service_type_ids?.includes(service_type_id) && assoc?.category_id === category_id,
  );
};

export const isCategoryValid = (
  row: PricePlanModalRow | Row,
  serviceTypeAssocs: SahServiceTypeAssociation[],
  categories: any[],
) => {
  const { category_id } = row ?? {};

  const assoc = findRowAssoc(row, serviceTypeAssocs);
  const category = categories.find((cat) => cat.id === category_id);

  return Boolean(assoc && category);
};

/**
 * The date range a price plan entry applies over, taken from the period of the Support at Home
 * service its association points at. An entry whose association cannot be resolved has no range
 * to show.
 */
export const deriveRowServicePeriod = (
  row: PricePlanModalRow | Row,
  serviceTypeAssocs: SahServiceTypeAssociation[],
): string => {
  const assoc = findRowAssoc(row, serviceTypeAssocs);
  return formatServicePeriod(assoc?.start_date, assoc?.end_date);
};

/**
 * What a price plan entry is actually tied to, as popover lines.
 *
 * Names the Support at Home service and the period it covers. Both matter now that the same local
 * service type can appear in a plan more than once: the rows are otherwise identical apart from
 * their category, and the thing that really distinguishes them is which occurrence of the
 * government service they resolve to.
 */
export const deriveRowServiceDetail = (
  row: PricePlanModalRow | Row,
  serviceTypeAssocs: SahServiceTypeAssociation[],
): string[] => {
  const assoc = findRowAssoc(row, serviceTypeAssocs);
  if (!assoc) return [];

  const period = formatServicePeriod(assoc.start_date, assoc.end_date);
  const sahId = assoc.service_info?.sah_id;

  return [
    assoc.name && `Connected to ${sahId ? `${sahId} - ` : ''}${assoc.name}`,
    period && `Service period: ${period}`,
  ].filter(Boolean) as string[];
};

export const validServiceType = (row: PricePlanModalRow | Row, serviceTypes: any[]) => {
  return serviceTypes.find((st) => st.id === row.service_type_id);
};
